import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Alert,
    Box,
    Card,
    CardContent,
    LinearProgress,
    TextField,
    Typography,
} from '@mui/material';
import { addMonths } from 'date-fns';
import { FileDownload as FileDownloadIcon, FilterList, Search } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { SCUIContent } from '../../atomic/organisms';
import { SCUIButton } from '../../atomic/atoms/SCUIButton/SCUIButton';
import AIStudioAnalyticsQuery from '../../services/query/AIStudioAnalyticsQuery';
import { downloadExcel } from '../../utils/excelFileHelper';
import { ExtendedGridColDef } from '@/types/ExtendedGridColDef';
import { PGR } from './productGenerationReport.classes';

interface ReportRow {
    id: string;
    username: string;
    productBarcode: string;
    generationCount: number;
    lastCreatedDate: string;
}

function parseYmd(s: string): Date | null {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) {
        return null;
    }
    const d = new Date(`${s}T12:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
}

function formatYmd(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

/** Başlangıçtan tam 1 takvim ayı sonraki gün (bitiş bu tarihe kadar seçilebilir, dahil). */
function addOneCalendarMonthYmd(ymd: string): string {
    const d = parseYmd(ymd);
    if (!d) {
        return ymd;
    }
    return formatYmd(addMonths(d, 1));
}

/** Yerel saat ile YYYY-MM-DD (filtre varsayılanı / boş tarih yedeği). */
function getTodayYmd(): string {
    const n = new Date();
    const y = n.getFullYear();
    const m = String(n.getMonth() + 1).padStart(2, '0');
    const day = String(n.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

export const ProductGenerationReport: FC = () => {
    const { t, i18n } = useTranslation();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [rows, setRows] = useState<ReportRow[]>([]);
    const [totalCount, setTotalCount] = useState(0);

    const [filterUsername, setFilterUsername] = useState('');
    const [filterStartDate, setFilterStartDate] = useState(() => getTodayYmd());
    const [filterEndDate, setFilterEndDate] = useState(() => getTodayYmd());
    const [applied, setApplied] = useState<{ username: string; startDate: string; endDate: string }>(() => {
        const today = getTodayYmd();
        return { username: '', startDate: today, endDate: today };
    });

    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params: {
                username?: string;
                startDate?: string;
                endDate?: string;
                pageNumber: number;
                pageSize: number;
            } = {
                pageNumber: paginationModel.page + 1,
                pageSize: paginationModel.pageSize,
            };
            if (applied.username.trim()) {
                params.username = applied.username.trim();
            }
            const start = applied.startDate || getTodayYmd();
            const end = applied.endDate || getTodayYmd();
            params.startDate = start;
            params.endDate = end;

            const res = await AIStudioAnalyticsQuery.GetProductGenerationReport(params);
            const items = res?.items ?? res?.Items ?? [];
            const total = res?.totalCount ?? res?.TotalCount ?? 0;

            const mapped: ReportRow[] = items.map(
                (r: {
                    username?: string;
                    productBarcode?: string;
                    generationCount?: number;
                    lastCreatedDate?: string;
                }, index: number) => ({
                    id: `${r.username ?? ''}-${r.productBarcode ?? ''}-${index}`,
                    username: r.username ?? '',
                    productBarcode: r.productBarcode ?? '',
                    generationCount: r.generationCount ?? 0,
                    lastCreatedDate: r.lastCreatedDate ?? '',
                })
            );

            setRows(mapped);
            setTotalCount(total);
        } catch {
            setError(t('productGenerationReport.error'));
            setRows([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
        }
    }, [applied, paginationModel.page, paginationModel.pageSize, t]);

    useEffect(() => {
        load();
    }, [load]);

    const handleApplyFilter = () => {
        const today = getTodayYmd();
        const start = filterStartDate || today;
        const end = filterEndDate || today;
        setFilterStartDate(start);
        setFilterEndDate(end);
        setApplied({
            username: filterUsername.trim(),
            startDate: start,
            endDate: end,
        });
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
    };

    const handleClearFilter = () => {
        const today = getTodayYmd();
        setFilterUsername('');
        setFilterStartDate(today);
        setFilterEndDate(today);
        setApplied({ username: '', startDate: today, endDate: today });
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
    };

    const handleStartDateChange = (value: string) => {
        const next = value || getTodayYmd();
        setFilterStartDate(next);
        const end = filterEndDate || next;
        if (end < next) {
            setFilterEndDate(next);
            return;
        }
        const maxEnd = addOneCalendarMonthYmd(next);
        if (end > maxEnd) {
            setFilterEndDate(maxEnd);
        }
    };

    const handleEndDateChange = (value: string) => {
        const next = value || getTodayYmd();
        const start = filterStartDate || next;
        if (next < start) {
            setFilterEndDate(next);
            setFilterStartDate(next);
            return;
        }
        const maxEnd = addOneCalendarMonthYmd(start);
        const clamped = next > maxEnd ? maxEnd : next;
        setFilterEndDate(clamped);
    };

    const formatDate = (iso: string) => {
        if (!iso) {
            return '';
        }
        try {
            const d = new Date(iso);
            return new Intl.DateTimeFormat(i18n.language === 'tr-TR' ? 'tr-TR' : 'en-US', {
                dateStyle: 'short',
                timeStyle: 'short',
            }).format(d);
        } catch {
            return iso;
        }
    };

    const handleExportExcel = () => {
        if (!rows.length) {
            return;
        }
        const excelRows = rows.map((r) => ({
            username: r.username,
            productBarcode: r.productBarcode,
            generationCount: r.generationCount,
            lastCreatedDate: formatDate(r.lastCreatedDate),
        }));
        const now = new Date();
        const dd = String(now.getDate()).padStart(2, '0');
        const mm = String(now.getMonth() + 1).padStart(2, '0');
        const yyyy = now.getFullYear();
        const fileName = `ProductGenerationReport_${dd}.${mm}.${yyyy}.xlsx`;

        downloadExcel({
            excelDownloadData: excelRows,
            excelDownloadFolderName: 'Product-Generation-Report',
            fileNameOverride: fileName,
        });
    };

    const columns: ExtendedGridColDef[] = [
        {
            field: 'username',
            headerName: t('productGenerationReport.username'),
            flex: 1,
            minWidth: 160,
        },
        {
            field: 'productBarcode',
            headerName: t('productGenerationReport.barcode'),
            flex: 1,
            minWidth: 140,
        },
        {
            field: 'generationCount',
            headerName: t('productGenerationReport.count'),
            width: 120,
            type: 'number',
            align: 'center',
            headerAlign: 'center',
        },
        {
            field: 'lastCreatedDate',
            headerName: t('productGenerationReport.lastGenerationDate'),
            flex: 1,
            minWidth: 180,
            renderCell: (params) => formatDate(params.row?.lastCreatedDate as string),
        },
    ];

    return (
        <SCUIContent className={PGR.page} headerName={t('productGenerationReport.title')}>
            {loading && <LinearProgress className={PGR.loadingBar} sx={{ mb: 2 }} />}

            <Box className={PGR.content} sx={{ pt: { xs: 2, sm: 2, md: 3 } }}>
                <Card
                    className={PGR.filterCard}
                    elevation={3}
                    sx={{ mb: 3, borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}
                >
                    <CardContent>
                        <Box className={PGR.filterHeader} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <FilterList color="primary" />
                            <Typography className={PGR.filterTitle} variant="h6">
                                {t('aiStudioAnalytics.filter.title')}
                            </Typography>
                        </Box>
                        <Box className={PGR.filterRow} sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
                            <TextField
                                className={PGR.inputUsername}
                                size="small"
                                label={t('aiStudioAnalytics.filter.username')}
                                placeholder={t('aiStudioAnalytics.filter.usernamePlaceholder')}
                                value={filterUsername}
                                onChange={(e) => setFilterUsername(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        handleApplyFilter();
                                    }
                                }}
                                sx={{ minWidth: 200 }}
                                slotProps={{
                                    input: {
                                        startAdornment: <Search sx={{ mr: 0.5, color: 'action.disabled' }} />,
                                    },
                                }}
                            />
                            <TextField
                                className={PGR.inputStartDate}
                                required
                                size="small"
                                type="date"
                                label={t('aiStudioAnalytics.filter.startDate')}
                                value={filterStartDate}
                                onChange={(e) => handleStartDateChange(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        handleApplyFilter();
                                    }
                                }}
                                sx={{ minWidth: 180 }}
                                slotProps={{
                                    inputLabel: { shrink: true },
                                    // Başlangıç serbest; bitiş üst sınırı onChange ile (başlangıç + 1 ay) hizalanır.
                                }}
                            />
                            <TextField
                                className={PGR.inputEndDate}
                                required
                                size="small"
                                type="date"
                                label={t('aiStudioAnalytics.filter.endDate')}
                                value={filterEndDate}
                                onChange={(e) => handleEndDateChange(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        handleApplyFilter();
                                    }
                                }}
                                sx={{ minWidth: 180 }}
                                slotProps={{
                                    inputLabel: { shrink: true },
                                    htmlInput: {
                                        min: filterStartDate || undefined,
                                        max: filterStartDate
                                            ? addOneCalendarMonthYmd(filterStartDate)
                                            : undefined,
                                    },
                                }}
                            />
                            <Box
                                className={PGR.filterActions}
                                sx={{
                                    display: 'inline-flex',
                                    gap: 1,
                                    flexWrap: 'wrap',
                                    alignItems: 'center',
                                    '& button': { minWidth: 132 },
                                }}
                            >
                                <SCUIButton
                                    className={PGR.buttonApply}
                                    text={t('aiStudioAnalytics.filter.apply')}
                                    type="primary"
                                    size="m"
                                    sizeType="fixed"
                                    onClick={handleApplyFilter}
                                />
                                <SCUIButton
                                    className={PGR.buttonClear}
                                    text={t('aiStudioAnalytics.filter.clear')}
                                    type="secondary"
                                    size="m"
                                    sizeType="fixed"
                                    onClick={handleClearFilter}
                                />
                            </Box>
                        </Box>
                    </CardContent>
                </Card>

                {error && (
                    <Alert className={PGR.errorAlert} severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Card className={PGR.gridCard} elevation={3} sx={{ borderRadius: 2 }}>
                    <CardContent>
                        <Box
                            className={PGR.gridHeaderRow}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                flexWrap: 'wrap',
                                gap: 1,
                                mb: 2,
                            }}
                        >
                            <Box className={PGR.gridTitleGroup} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <FilterList color="primary" />
                                <Typography className={PGR.gridTitle} variant="h6">
                                    {t('productGenerationReport.gridTitle')}
                                </Typography>
                            </Box>
                            <SCUIButton
                                className={PGR.buttonExportExcel}
                                text={t('general.downloadExcel')}
                                type="secondary"
                                size="m"
                                sizeType="fixed"
                                showStartIcon
                                startIcon={<FileDownloadIcon />}
                                onClick={handleExportExcel}
                                disabled={loading || rows.length === 0}
                                style={{ textTransform: 'none' }}
                            />
                        </Box>
                        <Box className={PGR.dataGridWrap} sx={{ width: '100%', height: 560 }}>
                            <DataGrid
                                className={PGR.dataGrid}
                                rows={rows}
                                columns={columns}
                                loading={loading}
                                paginationMode="server"
                                rowCount={totalCount}
                                paginationModel={paginationModel}
                                onPaginationModelChange={setPaginationModel}
                                pageSizeOptions={[25, 50, 100]}
                                disableRowSelectionOnClick
                                localeText={{
                                    noRowsLabel: t('productGenerationReport.noData'),
                                }}
                            />
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        </SCUIContent>
    );
};
